import React, { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import Info from '../../../../../components/channel-configuration/info';
import { VersionedFeatureType } from '../../../../../data-access/gql-types/graphql';
import { ChannelBox } from '../../../components/channel/box';
import { useDevicesAndChannelsContext } from '../../../context/devices-and-channels';
import { useCheckDeviceSupport } from '../../../hooks/version-map/use-check-device-support';
import { useGateSupportedFeatures } from '../../channel-details/gate/hooks/use-gate-supported';
import { Calibration } from '../../device-settings/device-settings-form/sections';
import AlertConfig from '../components/alert-config';
import NegativeOutput from '../components/negative-output';
import PartialSbsSignalTime from '../components/partial-sbs-time';
import SbsSignalTime from '../components/sbs-signal-time';
import TiltTime from '../components/tilt-time';
import { useGateConfiguration } from '../hooks/use-gate-configuration';
import { InfoPageType } from '../types';

type GateConfigurationProps = {
  setInfoPage: Dispatch<SetStateAction<InfoPageType>>;
};

const GateConfiguration: React.FC<GateConfigurationProps> = ({ setInfoPage }) => {
  const { t } = useTranslation('configuration');
  const { channel } = useDevicesAndChannelsContext();
  const {
    partialControlModeSupported,
    setTiltTimeSupported,
    calibrationSupported,
    stepByStepSupported,
    setNegativeOutputSupported,
  } = useGateSupportedFeatures(channel);
  const { checkSupport } = useCheckDeviceSupport();
  const {
    handleSaveSignalTime,
    handleSaveAlert,
    handleSavePartialSignalTime,
    handleSaveTiltTime,
    handleSetNegativeOutput,
  } = useGateConfiguration();

  const alertSupported = checkSupport({
    feature: VersionedFeatureType.GateAlerts,
    supportKey: 'Gate',
    currentVersion: channel?.currentFirmwareVersion,
  });

  if (!channel) return null;

  return (
    <>
      <Info title={t('channelTest')} onClick={() => setInfoPage(InfoPageType.CHANNEL)} />
      <ChannelBox channel={channel} isListItem noRedirect />
      {stepByStepSupported && <SbsSignalTime channel={channel} handleSave={handleSaveSignalTime} />}
      {partialControlModeSupported && (
        <PartialSbsSignalTime channel={channel} handleSave={handleSavePartialSignalTime} />
      )}
      {setTiltTimeSupported && <TiltTime channel={channel} handleSave={handleSaveTiltTime} />}
      {setNegativeOutputSupported && <NegativeOutput channel={channel} handleSave={handleSetNegativeOutput} />}
      {alertSupported && <AlertConfig channel={channel} handleSave={handleSaveAlert} />}

      {calibrationSupported && (
        <>
          <hr />
          <Info title={t('calibration')} onClick={() => setInfoPage(InfoPageType.CALIBRATION)} />
          <Calibration channel={channel} />
        </>
      )}
    </>
  );
};

export default GateConfiguration;
